import React from "react";
import { useState, useEffect, useRef }  from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import "./MaintenanceRequest.css";
import { taskService } from '../services/taskService';
import { equipmentService } from '../../equipment/services/equipmentService';
import profileIcon from '../../../shared/assets/icons/profile.png';
import bellIcon from '../../../shared/assets/icons/bell.png'; 
import micIcon from '../../../shared/assets/icons/micIcon.png';
import plusIcon from '../../../shared/assets/icons/plusIcon.png';
import backIcon from '../../../shared/assets/icons/back.png';
import deleteIcon from '../../../shared/assets/icons/delete.png';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { getUserData } from '../../../shared/utils/authStorage';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import HeaderNav from "../../../shared/components/HeaderNav";
import { IoMdArrowBack } from "react-icons/io";
import { IoArrowUndoOutline } from "react-icons/io5";
import { MdDownloadDone } from "react-icons/md";
import MobileFooterNav from "../../../shared/components/MobileFooterNav";

const { webkitSpeechRecognition: SpeechRecognition } = window;

const MaintenanceRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const [equipmentDatas, setEquipmentDatas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [equipmentPage, setEquipmentPage] = useState(1);
  const [equipmentHasMore, setEquipmentHasMore] = useState(true);
  const equipmentSearchRef = useRef('');
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressSearchTerm, setAddressSearchTerm] = useState("");
  const [isLoadingEquipment, setIsLoadingEquipment] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const [issueText, setIssueText] = useState("");
  const [issueList, setIssueList] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(2);

  const priorityOptions = [
    { label: 'Low', value: 0 },
    { label: 'Medium', value: 1 },
    { label: 'Normal', value: 2 },
    { label: 'High', value: 3 },
    { label: 'Urgent', value: 4 },
  ];

  let recognition = null;

const handleSelect = (item) => {
    setSearchTerm(item.name);
    setSelectedEquipment(item);
  };

  const handleIssueChange = (e) => {
    setIssueText(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddIssue();
    }
  };

  const handleAddIssue = () => {
    if (issueText.trim() !== "") {
      setIssueList([...issueList, issueText]);
      setIssueText("");
    }
  };
  
  const handleDeleteIssue = (index) => {
    const updatedList = issueList.filter((_, i) => i !== index);
    setIssueList(updatedList);
  };

  const addMaintenanceRequest = async () => {
    if (!selectedEquipment) {
      showSnackbar('Equipment is required', 'error');
      return;
    }

    if (issueList.length === 0) {
      showSnackbar('Issues are required', 'error');
      return;
    }


    const userData = getUserData();
    try {
      const requestData = {
        equipment_id: selectedEquipment?.id,
        reports: {
          issue_details: issueList,
        },
        item_category_id: selectedEquipment.productId,
        reported_by: {
          name: userData.customer.company_name,
          phone_number: userData.phone_number,
        },
        address_id: selectedAddress?.id,
        priority: selectedPriority,
      };
      
      const data = await taskService.createMaintenanceRequest(requestData);
      navigate('/tasklist');
    } catch (error) {
      console.error(error);
      alert('Error creating maintenance request');
    }
  };

  useEffect(() => {
    if (!isListening) return;

    if (!("SpeechRecognition" in window) && !("webkitSpeechRecognition" in window)) {
      alert("Speech recognition is not supported in this browser.");
      setIsListening(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognition = new SpeechRecognition();

    recognition.lang = "en-US";
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    
    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript + " ";
      }
      setIssueText(transcript.trim());
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      alert("Speech recognition error: " + event.error);
      setIsListening(false);
    };

    recognition.start();

    return () => {
      recognition.stop();
    };
  }, [isListening]);

  const loadEquipment = async (searchQuery = '', page = 1) => {
    if (!equipmentHasMore && page > 1) return;
    setIsLoadingEquipment(true);
    try {
      const response = await taskService.fetchEquipment(searchQuery, page);
      if (response.success && Array.isArray(response.data)) {
        const formattedData = response.data.map(item => ({
          id: item.equipment_id,
          name: item.serial_identifier || `${item.category} - ${item.make}`,
          category: item.category,
          make: item.make,
          model: item.model,
          serialNumber: item.serial_number,
          address: item.address1,
          productId:item.product_id
        }));
        setEquipmentDatas(prev => page === 1 ? formattedData : [...prev, ...formattedData]);
        setEquipmentHasMore(response.data.length === 10);
      }
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setEquipmentDatas([]);
    } finally {
      setIsLoadingEquipment(false);
    }
  };

  const loadClientAddresses = async (searchQuery = '') => {
    setIsLoadingAddresses(true);
    try {
      const userData = getUserData();
      
      if (!userData?.customer?.id) {
        return;
      }
      
      const response = await taskService.getSearchClientAddress(searchQuery);
      
      if (Array.isArray(response)) {
        const formattedAddresses = response.map(item => {
          const address1 = item.address?.address1 || '';
          return {
            id: item.address_id,
            name: address1 || 'Address',
            fullAddress: item.address
          };
        });
        setAddresses(formattedAddresses);
      }
    } catch (error) {
      setAddresses([]);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  useEffect(() => {
    // Check if equipment data was passed from Dashboard scan
    if (location.state?.equipment) {
      const equip = location.state.equipment;
      setSelectedEquipment(equip);
      setSearchTerm(equip.name);
    }
    loadClientAddresses();
  }, [location.state]);

  return (
    <div className="maintenance-request">
      {/* Main Header */}
      <HeaderNav profileIcon={profileIcon} />
      {/* Second Header */}
      <div className="second-header">
        <button className="back-button">
          <IoArrowUndoOutline color="black" size={25} alt="Back" onClick={() => navigate('/tasklist')} />
          {/* <img src={backIcon} alt="Back" className="icon back-icon" onClick={() => navigate('/tasklist')} /> */}
        </button>
        <h2 className="page-title">New Request</h2>
      </div>

      {/* Mobile Header */}
      <header className="mobile-header">
        <div className="header-left">
           <IoArrowUndoOutline color="black" size={25} alt="Back" onClick={() => navigate('/tasklist')} />
        </div>
        <h1 className="app-title">Add Requests</h1>
      </header>

      {/* Form Section */}
      <div className="form-container">
        <form className="maintenance-form" onSubmit={(e) => { e.preventDefault(); addMaintenanceRequest(); }}>
          <div className="form-group">
            <SearchableDropdown
              options={equipmentDatas}
              value={searchTerm}
              onChange={(term) => {
                setSearchTerm(term);
                equipmentSearchRef.current = term;
                if (term === '') {
                  setSelectedEquipment(null);
                }
                setEquipmentPage(1);
                setEquipmentHasMore(true);
                loadEquipment(term, 1);
              }}
              onClear={() => {
                setSearchTerm('');
                setSelectedEquipment(null);
                setEquipmentPage(1);
                setEquipmentHasMore(true);
                loadEquipment('', 1);
              }}
              onSelect={handleSelect}
              placeholder="Search Equipment *"
              trailingIcon={plusIcon}
              closeIcon={deleteIcon}
              selectedItem={selectedEquipment}
              onTrailingIconClick={() => navigate('/equipment')}
              loading={isLoadingEquipment}
              disableLocalFilter={true}
              containerClassName="dropdown-container"
              className="dropdown-input"
              onFocus={() => {
                if (equipmentDatas.length === 0) {
                  loadEquipment('', 1);
                }
              }}
              onScrollEnd={() => {
                if (!isLoadingEquipment && equipmentHasMore) {
                  const nextPage = equipmentPage + 1;
                  setEquipmentPage(nextPage);
                  loadEquipment(equipmentSearchRef.current, nextPage);
                }
              }}
            />
          </div>

          <div className="form-group">
            <SearchableDropdown
              options={addresses}
              value={addressSearchTerm}
              onChange={(term) => {
                setAddressSearchTerm(term);
                if (term === '') {
                  setSelectedAddress(null);
                }
                if (term.length >= 1) {
                  loadClientAddresses(term);
                } else {
                  loadClientAddresses('');
                }
              }}
              onClear={() => {
                setAddressSearchTerm('');
                setSelectedAddress(null);
                loadClientAddresses('');
              }}
              onSelect={(item) => {
                setSelectedAddress(item);
                setAddressSearchTerm(item.name);
              }}
              onFocus={() => {
                if (addresses.length === 0) {
                  loadClientAddresses('');
                }
                if (addressSearchTerm === '') {
                  setAddressSearchTerm(' ');
                }
              }}
              placeholder="Select Address"
              selectedItem={selectedAddress}
              loading={isLoadingAddresses}
              disableLocalFilter={true}
              containerClassName="dropdown-container"
              className="dropdown-input"
            />
          </div>

          <div className="form-group">
            <p className="field-heading">Priority</p>
            <div className="priority-selector">
              {priorityOptions.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  className={`priority-chip priority-chip--${p.label.toLowerCase()} ${selectedPriority === p.value ? 'priority-chip--active' : ''}`}
                  onClick={() => setSelectedPriority(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

<div className="form-group">
  <p className="field-heading">Common Issues</p>
  <div className="issue-chips">
    {['Not working', 'Leaking', 'Noise issue', 'Power failure', 'Overheating', 'Broken part', 'Needs servicing', 'Error displayed'].map((issue) => (
      <button
        key={issue}
        type="button"
        className="issue-chip"
        onClick={() => { if (!issueList.includes(issue)) setIssueList(prev => [...prev, issue]); }}
      >
        + {issue}
      </button>
    ))}
  </div>
</div>

<div className="form-group">
  <p className="field-heading">Describe Issues</p>
  <div className="issue-input-wrapper">
    <textarea
      id="issues"
      rows="1"
      placeholder="Describe the issues *"
      value={issueText}
      onChange={handleIssueChange}
      onKeyPress={handleKeyPress}
      className="montserrat-18 issues-textarea"
    />

    <div className="issue-actions">

      {/* Voice Note Button */}
              
      <button
        type="button"
        className={`voice-button ${isListening ? "recording" : ""}`}
        onClick={() => setIsListening((prev) => !prev)}
      >
        <img src={micIcon} alt="voice" className="voice-icon" />
        {isListening ? "Recording..." : "Add Voice"}
      </button>

      {/* Add Issue Button */}

          <button
        type="button"
        onClick={handleAddIssue}
        className="add-btn-voice"
      >
        <span>Add</span>
        <MdDownloadDone size={22} />
      </button>

    </div>
  </div>
</div>

          {/* Displaying List of Added Issues */}
          <ol className="issue-list">
            {issueList.map((item, index) => (
              <li key={index} className="issue-item">
                <span className="issue-bullet">{index + 1}.</span>
                <span className="issue-text">{item}</span>
                <img 
                  src={deleteIcon} 
                  alt="Delete" 
                  className="icon delete-icon" 
                  onClick={() => handleDeleteIssue(index)}
                />
              </li>
            ))}
          </ol>



          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={() => navigate('/tasklist')}>
              Cancel
            </button>
            <button type="button" className="save-button" onClick={addMaintenanceRequest}>
              Submit
            </button>
          </div>
        </form>
      </div>
        <MobileFooterNav />
    </div>
  );
};

export default MaintenanceRequest;